import { Head, Link } from "@inertiajs/react"
import { Fragment, useMemo, useState } from "react"
import { ChevronDown, Pencil, Printer } from "lucide-react"

import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { SegmentedControl } from "@/components/ui/segmented-control"
import { computeAddOns, roomFigures, surfaceFigures } from "@/editor-core/addons"
import { computeAllRooms, rollAssignments } from "@/components/editor/quantities"
import {
  type CutPlans,
  type Subfloor,
  type WasteMode,
  type WireAddOn,
  type WireProductAddOn,
  type WireRoom,
  type WireSurface,
  addOnsFromServer,
  roomsFromServer,
  rulesFromServer,
  surfacesFromServer,
} from "@/editor-core/document"
import { EM_DASH } from "@/editor-core/labels"
import { type PlacedImage } from "@/editor-core/schedule"
import { toSeamSettings } from "@/editor-core/seams"
import {
  type WorksheetLine,
  byRoomPivot,
  worksheetLines,
  worksheetTotals,
} from "@/editor-core/worksheet"
import { type HeaderPair, HeaderColumn } from "@/components/print/PrintSheetHeader"
import { cn } from "@/lib/utils"
import type { AdminSettings } from "@/types/inertia"

// The order worksheet — "how much of everything to buy". One row per product
// aggregated across the takeoff, add-ons as their own rows, rooms as an
// expandable child level, quantities in the unit the product dictates. NO
// prices — that is the bid's job (milestone 9), and the material schedule
// answers "what goes where".
//
// Everything here derives in one useMemo chain from the same editor-core
// modules the editor renders from, so the printed totals and the panel's can
// never disagree. Derived cells are read-only by construction: the page has
// no inputs, and the one typed quantity (a surface's) is visibly flagged.

type Props = {
  takeoff: {
    id: number
    name: string
    url: string
    owner: { kind: "plan" | "project"; id: number; display_name: string; url: string }
  }
  header: { title: string; subtitle: string | null; left: HeaderPair[]; right: HeaderPair[] }
  document: {
    rooms: WireRoom[]
    surfaces: WireSurface[]
    add_ons: WireAddOn[]
    cut_plans: CutPlans
  }
  product_add_ons: WireProductAddOn[]
  settings: AdminSettings
  calibrations: { sheet_id: number; label: string; ft_per_px: number | null }[]
  sheet_images: Record<number, PlacedImage[]>
  project_subfloor: Subfloor
  waste_mode_default: WasteMode
}

const COLUMNS = ["Product / Add-on", "Rooms", "Net", "Cut waste", "Margin", "Order", "Unit", "Ctn / Rolls", "LF"]

const fmt = (n: number | null): string =>
  n == null ? EM_DASH : Number.isInteger(n) ? String(n) : n.toFixed(1)

export default function WorksheetPage(props: Props) {
  const { takeoff, header } = props
  const [pivot, setPivot] = useState<"product" | "room">("product")
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [allExpanded, setAllExpanded] = useState(false)

  const { lines, unassignedRooms, roomGroups, totals } = useMemo(() => {
    const rooms = roomsFromServer(props.document.rooms)
    const surfaces = surfacesFromServer(props.document.surfaces)
    const settings = toSeamSettings(props.settings)
    const calibBySheet = Object.fromEntries(
      props.calibrations
        .filter((c) => c.ft_per_px != null)
        .map((c) => [c.sheet_id, { ftPerPx: c.ft_per_px, calibPts: [] as [number, number][] }])
    )
    const allComputed = computeAllRooms(rooms, calibBySheet, settings, props.waste_mode_default)
    const assignments = rollAssignments(rooms, calibBySheet, props.document.cut_plans, settings)
    const computedAddOns = computeAddOns({
      addOns: addOnsFromServer(props.document.add_ons),
      rules: rulesFromServer(props.product_add_ons),
      rooms: rooms.map((room) =>
        roomFigures(
          room,
          calibBySheet[room.sheetId]?.ftPerPx ?? null,
          props.sheet_images[room.sheetId] ?? [],
          props.project_subfloor
        )
      ),
      surfaces: surfaces.map((surface) => surfaceFigures(surface, props.project_subfloor)),
    })
    const { lines, unassignedRooms } = worksheetLines({
      rooms: allComputed,
      surfaces,
      addOns: computedAddOns,
      assignments,
    })
    return {
      lines,
      unassignedRooms,
      roomGroups: byRoomPivot(lines, [
        ...allComputed.map((r) => r.id),
        ...surfaces.map((s) => s.id),
      ]),
      totals: worksheetTotals(lines),
    }
  }, [props])

  const issues = useMemo(
    () =>
      lines.flatMap((line) => {
        const out: string[] = []
        if (line.incompatible)
          out.push(
            `${line.label}: the unit and method can't produce a number — set a coverage rate or change the method.`
          )
        for (const zero of line.zeroRows) out.push(`${line.label}: 0 in ${zero.label} — ${ZERO_WORDS[zero.reason]}.`)
        if (line.unmeasured.length > 0)
          out.push(`${line.label}: ${line.unmeasured.join(", ")} not measured — calibrate the sheet.`)
        return out
      }),
    [lines]
  )

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  const expandAll = () => {
    setAllExpanded((v) => !v)
    setExpanded(allExpanded ? new Set() : new Set(lines.map((l) => l.key)))
  }

  const title = `Order Worksheet — ${header.title}`
  const description =
    "Total material to order for this takeoff — every product and add-on with net, waste, and order quantities in purchasing units."

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="print-chrome mb-4 flex flex-wrap items-center gap-3">
          <Link href={takeoff.url} className="no-underline hover:underline">
            ← {takeoff.name}
          </Link>
          <span data-testid="worksheet-pivot">
            <SegmentedControl
              options={[
                { value: "product", label: "By product" },
                { value: "room", label: "By room" },
              ]}
              value={pivot}
              onChange={(value) => setPivot(value as "product" | "room")}
            />
          </span>
          <span className="ml-auto flex gap-2">
            {pivot === "product" && (
              <Button variant="ghost" onClick={expandAll} data-testid="worksheet-expand-all">
                {allExpanded ? "Collapse rooms" : "Expand rooms"}
              </Button>
            )}
            <Button variant="soft" onClick={() => window.print()} data-testid="print-worksheet">
              <Printer className="mr-1.5 h-4 w-4" />
              Print / Save as PDF
            </Button>
          </span>
        </div>

        <article className="print-sheet mx-auto max-w-5xl rounded-lg border border-hairline bg-surface p-6">
          <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-hairline pb-3">
            <h1>Order Worksheet</h1>
            <p className="text-ink-display">
              {header.title}
              {header.subtitle && <span className="text-ink-muted"> · {header.subtitle}</span>}
            </p>
          </header>

          <div className="mt-4 grid gap-x-8 gap-y-1 sm:grid-cols-2">
            <HeaderColumn pairs={header.left} />
            <HeaderColumn pairs={header.right} />
          </div>

          {(issues.length > 0 || unassignedRooms.length > 0) && (
            <div className="callout mt-4" data-testid="worksheet-issues">
              <ul className="list-disc pl-4">
                {unassignedRooms.length > 0 && (
                  <li>No product yet: {unassignedRooms.join(", ")} — these rooms total nothing below.</li>
                )}
                {issues.map((issue, i) => (
                  // Index-keyed on purpose: two identical add-ons produce
                  // identical issue strings, and duplicate keys drop rows.
                  <li key={`${i}-${issue}`}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          {pivot === "product" ? (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full border-collapse text-sm" data-testid="worksheet-table">
                <thead>
                  <tr className="border-b border-ink-muted text-left align-bottom text-xs uppercase tracking-wide text-ink-muted">
                    {COLUMNS.map((column, i) => (
                      <th
                        key={column}
                        className={cn("px-1.5 pb-1 font-semibold", i >= 1 && "text-right")}
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr>
                      <td colSpan={COLUMNS.length} className="px-1.5 py-4 text-ink-muted">
                        Nothing to order yet — assign products in the editor.
                      </td>
                    </tr>
                  ) : (
                    lines.map((line) => (
                      <Fragment key={line.key}>
                        <LineRow
                          line={line}
                          expanded={expanded.has(line.key)}
                          onToggle={() => toggle(line.key)}
                        />
                        {line.rooms.map((detail) => (
                          // Collapsed via the `hidden` CLASS, not the attribute:
                          // Tailwind's preflight [hidden] rule is !important
                          // inside a cascade layer, which would out-rank the
                          // print sheet's always-expand override.
                          <tr
                            key={`${line.key}-${detail.rowId}-${detail.label}`}
                            data-testid={`worksheet-child-${line.key}-${detail.rowId}`}
                            className={cn(
                              "worksheet-child border-b border-hairline text-xs text-ink-muted",
                              !expanded.has(line.key) && "hidden"
                            )}
                          >
                            <td className="px-1.5 py-1 pl-8">
                              {detail.label}
                              {detail.multiplier > 1 && ` ×${detail.multiplier}`}
                              <span className="ml-2">
                                {detail.mode === "computed"
                                  ? "· cut plan"
                                  : detail.mode === "net"
                                    ? "· net"
                                    : detail.manual
                                      ? "· typed"
                                      : ""}
                              </span>
                            </td>
                            <td />
                            <td className="px-1.5 py-1 text-right tabular-nums">
                              {fmt(detail.netQty)}
                            </td>
                            <td />
                            <td className="px-1.5 py-1 text-right tabular-nums">
                              {detail.marginPct != null ? `${detail.marginPct}%` : EM_DASH}
                            </td>
                            <td className="px-1.5 py-1 text-right tabular-nums">
                              {fmt(detail.orderQty)}
                            </td>
                            {/* A typed EA/LF figure keeps its own unit. */}
                            <td className="px-1.5 py-1 text-right">
                              {detail.unit ?? line.unit}
                            </td>
                            <td />
                            <td />
                          </tr>
                        ))}
                      </Fragment>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="border-t border-ink-muted text-sm font-semibold">
                    <td className="px-1.5 py-2" data-testid="worksheet-totals">
                      Σ totals
                    </td>
                    <td colSpan={4} />
                    <td className="px-1.5 py-2 text-right tabular-nums" colSpan={2}>
                      {[
                        totals.sf > 0 && `${fmt(totals.sf)} SF`,
                        totals.sy > 0 && `${fmt(totals.sy)} SY`,
                      ]
                        .filter(Boolean)
                        .join(" · ") || EM_DASH}
                    </td>
                    <td className="px-1.5 py-2 text-right tabular-nums">
                      {totals.cartons > 0 ? `${totals.cartons} ctn` : EM_DASH}
                      {totals.rolls > 0 && ` · ${totals.rolls} ${totals.rolls === 1 ? "roll" : "rolls"}`}
                    </td>
                    <td className="px-1.5 py-2 text-right tabular-nums">
                      {totals.lf > 0 ? fmt(totals.lf) : EM_DASH}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full border-collapse text-sm" data-testid="worksheet-by-room">
                <thead>
                  <tr className="border-b border-ink-muted text-left align-bottom text-xs uppercase tracking-wide text-ink-muted">
                    <th className="px-1.5 pb-1 font-semibold">Room</th>
                    <th className="px-1.5 pb-1 font-semibold">Product / Add-on</th>
                    <th className="px-1.5 pb-1 text-right font-semibold">Net</th>
                    <th className="px-1.5 pb-1 text-right font-semibold">Order</th>
                    <th className="px-1.5 pb-1 text-right font-semibold">Unit</th>
                    <th className="px-1.5 pb-1 text-right font-semibold">Basis</th>
                  </tr>
                </thead>
                <tbody>
                  {roomGroups.map((group) => (
                    <Fragment key={group.rowId ?? "flat"}>
                      {group.entries.map((entry, i) => (
                        <tr
                          key={`${group.rowId}-${entry.key}`}
                          className="schedule-row border-b border-hairline"
                        >
                          <td className="px-1.5 py-2 font-medium">
                            {i === 0 ? group.label : ""}
                          </td>
                          <td className={cn("px-1.5 py-2", entry.kind === "addon" && "italic")}>
                            {entry.label}
                          </td>
                          <td className="px-1.5 py-2 text-right tabular-nums">
                            {fmt(entry.netQty)}
                          </td>
                          <td className="px-1.5 py-2 text-right tabular-nums">
                            {fmt(entry.orderQty)}
                          </td>
                          <td className="px-1.5 py-2 text-right">{entry.unit}</td>
                          <td className="px-1.5 py-2 text-right text-xs text-ink-muted">
                            {entry.mode === "computed"
                              ? "cut plan"
                              : entry.mode ?? (entry.manual ? "typed" : EM_DASH)}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <footer className="mt-6 border-t border-hairline pt-2 text-xs text-ink-muted">
            Quantities only — pricing belongs to the bid. {header.title}
            {header.subtitle && ` · ${header.subtitle}`} · {takeoff.name}
          </footer>
        </article>
      </AppShell>
    </>
  )
}

function LineRow({
  line,
  expanded,
  onToggle,
}: {
  line: WorksheetLine
  expanded: boolean
  onToggle: () => void
}) {
  const issue = computedIssueFor(line)
  return (
    <tr
      data-testid={`worksheet-line-${line.key}`}
      className="schedule-row border-b border-hairline align-top"
    >
      <td className={cn("px-1.5 py-2", line.parentKey != null && "pl-6")}>
        <span className="flex items-center gap-1.5">
          {line.rooms.length > 0 ? (
            <button
              type="button"
              aria-expanded={expanded}
              onClick={onToggle}
              data-testid={`worksheet-expand-${line.key}`}
              className="print-chrome -ml-1 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded text-ink-muted hover:bg-surface hover:text-ink-display"
            >
              <ChevronDown
                className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")}
              />
            </button>
          ) : (
            <span className="print-chrome w-4 shrink-0" />
          )}
          {line.color && (
            <span
              className="h-3 w-3 shrink-0 rounded-sm"
              style={{ background: line.color }}
              aria-hidden="true"
            />
          )}
          <span className={cn("font-medium", line.kind === "addon" && "font-normal italic")}>
            {line.label}
          </span>
          {line.kind === "addon" && line.parentKey != null && (
            <Badge tone="muted" title="From a product rule">
              auto
            </Badge>
          )}
          {line.manual && (
            <span
              title="Includes typed quantities — not from the drawing"
              data-testid={`manual-flag-${line.key}`}
              className="text-ink-muted"
            >
              <Pencil className="h-3 w-3" aria-label="Includes typed quantities" />
            </span>
          )}
          {line.minApplied && <Badge tone="muted">min</Badge>}
          {issue && <Badge tone="signal">{issue}</Badge>}
        </span>
      </td>
      <td className="px-1.5 py-2 text-right tabular-nums">{line.roomCount || EM_DASH}</td>
      <td className="px-1.5 py-2 text-right tabular-nums">{fmt(line.netQty)}</td>
      <td className="px-1.5 py-2 text-right tabular-nums">{fmt(line.computedWasteQty)}</td>
      <td className="px-1.5 py-2 text-right tabular-nums">
        {line.marginPct === "mixed" ? "mixed" : line.marginPct != null ? `${line.marginPct}%` : EM_DASH}
      </td>
      <td className="px-1.5 py-2 text-right font-semibold tabular-nums">{fmt(line.orderQty)}</td>
      <td className="px-1.5 py-2 text-right">{line.unit}</td>
      <td className="px-1.5 py-2 text-right tabular-nums">
        {line.cartons != null ? `${line.cartons} ctn` : line.rolls != null ? `${line.rolls} ${line.rolls === 1 ? "roll" : "rolls"}` : EM_DASH}
      </td>
      <td className="px-1.5 py-2 text-right tabular-nums">{fmt(line.lf)}</td>
    </tr>
  )
}

/** The line's trust/issue badge: mode for products, problems for add-ons. */
function computedIssueFor(line: WorksheetLine): string | null {
  if (line.incompatible) return "needs attention"
  if (line.mode === "mixed") return "mixed modes"
  return null
}

const ZERO_WORDS: Record<string, string> = {
  no_holes: "no interior deductions traced",
  subfloor_filtered: "subfloor doesn't match the condition",
  type_filtered: "product type doesn't match the filter",
  zero_basis: "measured zero",
  no_geometry: "a surface has no geometry for this method",
}
